/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import {
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  pairwise,
  takeUntil,
  tap,
  withLatestFrom,
} from "rxjs";
import { CatchmentDto, StationDataDto, StationDto, WatershedDto, DataPointDto, StationSimulationDataDto } from "src/app/services/api";
import { RootState } from "src/app/store";
import * as WatershedActions from "src/app/store/watershed/actions";
import * as FromWatershed from "src/app/store/watershed/selectors";
import * as AppActions from "src/app/store/app/actions";
import * as FromApp from "src/app/store/app/selectors";
import { DOCUMENT } from "@angular/common";
import { ModalWidth } from "src/app/types";
import { BaseMapLayerConfig, DataMode } from "src/app/interfaces";
import { CatchmentNames, DataModeVariable, IslandsNames } from "src/app/enums";
import { ScrollService } from "src/app/services/scroll/sroll.service";
import { GROUPPED_CATCHMENTS } from "src/app/constants";
import { OrderStationsByAlertLevelPipe } from "src/app/pipes/order-stations-by-alert-level/order-stations-by-alert-level.pipe";
import { OrderStationsByPrecipitationPipe } from "src/app/pipes/order-stations-by-precipitation/order-stations-by-precipitation.pipe";

@Component({
  selector: "app-catchment",
  templateUrl: "./catchment.component.html",
  styleUrls: ["./catchment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatchmentComponent {
  private static instanceCounter = 0;
  private readonly instanceId: number;
  public readonly componentId: string;

  public dataModeVariable: typeof DataModeVariable = DataModeVariable;

  public watershedText: string = "Vessant";

  public selectedWaterLevelStationCode: string;
  public selectedDischargeStationCode: string;
  public selectedPrecipitationStationCode: string;
  public orderedHydroStations: StationDto[] = [];
  public orderedMeteoStations: StationDto[] = [];

  public readonly baseMapLayerConfig$: Observable<BaseMapLayerConfig> = this.store.select(FromWatershed.selectMapLayerConfig);

  public readonly refreshState$: Observable<boolean> = this.store.select(FromApp.selectRefreshState);

  public readonly modalWidth$: Observable<ModalWidth> = this.store.select(FromApp.selectModalWidth);

  public readonly dataMode$: Observable<DataMode> = this.store.select(FromWatershed.selectDataMode);

  public readonly watershedId$: Observable<number> = this.store.select(FromWatershed.selectWatershedId);
  public readonly catchmentId$: Observable<number> = this.store.select(FromWatershed.selectCatchmentId);
  public readonly islandId$: Observable<string> = this.store.select(FromWatershed.selectIslandId);
  public readonly watershed$: Observable<WatershedDto> = this.store.select(FromWatershed.selectWatershed);

  public readonly hoveredStationCode$: Observable<string> = this.store.select(FromWatershed.selectHoveredStationCode);

  public readonly catchment$: Observable<CatchmentDto> = this.store.select(FromWatershed.selectIslandCatchment);

  public readonly catchmentWaterLevelStationCode$: Observable<string> = this.store.select(
    FromWatershed.selectCatchmentWaterLevelStationCode,
  );

  public readonly catchmentWaterLevelStationData$: Observable<StationDataDto> = this.store.select(
    FromWatershed.selectCatchmentWaterLevelStationData,
  );

  public readonly catchmentWaterLevelSimulationStationData$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentWaterLevelSimulationStationData,
  );

  public readonly catchmentDischargeSimulationStationData$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentDischargeSimulationStationData,
  );

  public readonly catchmentDischargeStationCode$: Observable<string> = this.store.select(FromWatershed.selectCatchmentDischargeStationCode);

  public readonly catchmentDischargeStationData$: Observable<StationDataDto> = this.store.select(
    FromWatershed.selectCatchmentDischargeStationData,
  );

  public readonly catchmentPrecipitationStationCode$: Observable<string> = this.store.select(
    FromWatershed.selectCatchmentPrecipitationStationCode,
  );

  public readonly catchmentPrecipitation10Data$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentPrecipitation10Data,
  );

  public readonly catchmentPrecipitation60Data$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentPrecipitation60Data,
  );

  public readonly hasPrecipitation10Data$: Observable<boolean> = this.catchmentPrecipitation10Data$.pipe(map(data => !!data?.length));
  public readonly hasPrecipitation60Data$: Observable<boolean> = this.catchmentPrecipitation60Data$.pipe(map(data => !!data?.length));

  public readonly emptyPrecipitationData$: Observable<boolean> = combineLatest([
    this.hasPrecipitation10Data$,
    this.hasPrecipitation60Data$,
  ]).pipe(map(([has10, has60]) => !has10 && !has60));

  public readonly emptyDischargeData$: Observable<boolean> = this.catchmentDischargeStationData$.pipe(
    map(data => !data?.discharge?.length),
  );

  public readonly hasWaterLevelData$: Observable<boolean> = this.catchmentWaterLevelStationData$.pipe(
    map(data => !!data?.waterLevel?.length),
  );

  public readonly hasDischargeData$: Observable<boolean> = this.catchmentDischargeStationData$.pipe(map(data => !!data?.discharge?.length));

  public readonly emptyHidroData$: Observable<boolean> = combineLatest([this.hasWaterLevelData$, this.hasDischargeData$]).pipe(
    map(([hasWL, hasDischarge]) => !hasWL && !hasDischarge),
  );

  public readonly noPrecipitation10Data$: Observable<boolean> = this.hasPrecipitation10Data$.pipe(map(hasData => !hasData));
  public readonly noPrecipitation60Data$: Observable<boolean> = this.hasPrecipitation60Data$.pipe(map(hasData => !hasData));

  public readonly catchmentPrecipitationStation$: Observable<StationDto> = this.store.select(FromWatershed.catchmentPrecipitationStation);

  public readonly catchmentHydroStations$: Observable<StationDto[]> = this.store.select(
    FromWatershed.selectWatershedCatchmentHydroStations,
  );

  public readonly catchmentMeteoStations$: Observable<StationDto[]> = this.store.select(
    FromWatershed.selectWatershedCatchmentMeteoStations,
  );

  private unsubscribeSubject$: Subject<void>;
  public currentCatchmentId: number;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store<RootState>,
    public readonly translateService: TranslateService,
    private readonly scrollService: ScrollService,
  ) {
    this.instanceId = ++CatchmentComponent.instanceCounter;
    this.componentId = `catchment-${this.instanceId}`;
  }

  public ionViewWillEnter(): void {
    this.unsubscribeSubject$ = new Subject();

    this.selectWaterLevelStation(null);
    this.selectPrecipitationStation(null);

    this.catchmentHydroStations$
      .pipe(
        first(stations => !!stations?.length),
        withLatestFrom(this.catchmentWaterLevelStationCode$, this.catchmentDischargeStationCode$),
        tap(([stations, catchmentWaterLevelStationCode]) => {
          const sortedStations = stations.sort((a, b) => this.orderByStatus(a, b));
          this.orderedHydroStations = sortedStations;

          if (!catchmentWaterLevelStationCode) {
            this.selectWaterLevelStation(sortedStations[0].id);
          } else {
            this.selectWaterLevelStation(catchmentWaterLevelStationCode);
          }
        }),
      )
      .subscribe();

    combineLatest([this.catchmentMeteoStations$.pipe(filter(stations => !!stations?.length)), this.dataMode$])
      .pipe(
        distinctUntilChanged(([, modeA], [, modeB]) => modeA === modeB),
        tap(([stations, dataMode]) => {
          const orderStationsByPrecipitation = new OrderStationsByPrecipitationPipe();
          const orderedMeteoStations = orderStationsByPrecipitation.transform(stations, dataMode);
          this.orderedMeteoStations = orderedMeteoStations;
          this.selectPrecipitationStation(orderedMeteoStations[0].id);
        }),
      )
      .subscribe();

    combineLatest([this.catchmentWaterLevelStationCode$, this.catchmentDischargeStationCode$, this.catchmentPrecipitationStationCode$])
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        pairwise(),
        tap(
          ([
            [previousCatchmentWaterLevelStationCode, previousCatchmentDischargeStationCode, previousCatchmentPrecipitationStationCode],
            [catchmentWaterLevelStationCode, catchmentDischargeStationCode, catchmentPrecipitationStationCode],
          ]) => {
            if (
              previousCatchmentWaterLevelStationCode !== catchmentWaterLevelStationCode &&
              catchmentWaterLevelStationCode &&
              catchmentDischargeStationCode !== catchmentWaterLevelStationCode
            ) {
              this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentWaterLevelStationCode }));
            }

            if (
              previousCatchmentDischargeStationCode !== catchmentDischargeStationCode &&
              catchmentDischargeStationCode &&
              catchmentDischargeStationCode !== catchmentWaterLevelStationCode
            ) {
              this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentDischargeStationCode }));
            }

            if (previousCatchmentPrecipitationStationCode !== catchmentPrecipitationStationCode && catchmentPrecipitationStationCode) {
              this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentPrecipitationStationCode }));
            }
          },
        ),
      )
      .subscribe();

    this.catchmentId$
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        first(catchmentId => !!catchmentId),
        tap(catchmentId => {
          this.currentCatchmentId = catchmentId;
          this.loadCatchment(catchmentId);
        }),
      )
      .subscribe();

    this.refreshState$
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        filter(refreshState => !!refreshState),
        withLatestFrom(
          this.catchmentId$,
          this.catchmentWaterLevelStationCode$,
          this.catchmentDischargeStationCode$,
          this.catchmentPrecipitationStationCode$,
        ),
        first(([, catchmentId]) => !!catchmentId),
        tap(([, catchmentId, catchmentWaterLevelStationCode, catchmentDischargeStationCode, catchmentPrecipitationStationCode]) => {
          this.loadCatchment(catchmentId);

          if (catchmentWaterLevelStationCode) {
            this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentWaterLevelStationCode }));
            this.store.dispatch(WatershedActions.loadStationSimulationData({ stationId: catchmentWaterLevelStationCode }));
          }

          if (catchmentDischargeStationCode && catchmentDischargeStationCode !== catchmentWaterLevelStationCode) {
            this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentDischargeStationCode }));
            this.store.dispatch(WatershedActions.loadStationSimulationData({ stationId: catchmentDischargeStationCode }));
          }

          if (catchmentPrecipitationStationCode) {
            this.store.dispatch(WatershedActions.loadStationData({ stationCode: catchmentPrecipitationStationCode }));
          }
        }),
      )
      .subscribe();

    this.modalWidth$
      .pipe(
        first(),
        filter(modalWidth => modalWidth === "900px"),
        tap(modalWidth => this.document.documentElement.style.setProperty("--modal-width", modalWidth)),
      )
      .subscribe();
  }

  public ionViewWillLeave(): void {
    if (this.unsubscribeSubject$) {
      this.unsubscribeSubject$.next();
      this.unsubscribeSubject$.complete();
    }
  }

  public scrollToElement(element: HTMLElement): void {
    this.scrollService.scrollToElement(element);
  }

  public resetToIslandsView(): void {
    this.store.dispatch(WatershedActions.setCatchmentId({ catchmentId: null }));
    this.store.dispatch(WatershedActions.setWatershedId({ watershedId: null }));
    this.store.dispatch(WatershedActions.setIslandId({ islandId: null }));
    this.store.dispatch(WatershedActions.resetToIslandsView());
  }

  public async closeCatchment(): Promise<void> {
    this.modalWidth$
      .pipe(
        first(),
        filter(modalWidth => modalWidth === "900px"),
        tap(() => this.store.dispatch(AppActions.switchModalWidth())),
      )
      .subscribe();
    this.store.dispatch(WatershedActions.setCatchmentId({ catchmentId: null }));
  }

  public selectStation(station: StationDto): void {
    this.modalWidth$
      .pipe(
        first(),
        filter(modalWidth => modalWidth === "900px"),
        tap(() => this.store.dispatch(AppActions.switchModalWidth())),
      )
      .subscribe();

    this.store.dispatch(WatershedActions.setStationCode({ stationCode: station.id }));
  }

  public onMouseEnter(stationCode: string): void {
    this.store.dispatch(WatershedActions.setHoveredStationCode({ stationCode, hoveredStationCode: stationCode }));
  }

  public onMouseLeave(stationCode: string): void {
    this.store.dispatch(WatershedActions.setHoveredStationCode({ stationCode, hoveredStationCode: null }));
  }

  public selectWaterLevelStation(catchmentWaterLevelStationCode: string): void {
    this.selectHydroStation(catchmentWaterLevelStationCode);
  }

  private selectHydroStation(stationCode: string): void {
    this.selectedWaterLevelStationCode = stationCode;
    this.selectedDischargeStationCode = stationCode;
    this.store.dispatch(WatershedActions.setCatchmentWaterLevelStationCode({ catchmentWaterLevelStationCode: stationCode }));
    this.store.dispatch(WatershedActions.setCatchmentDischargeStationCode({ catchmentDischargeStationCode: stationCode }));

    // Load simulation data for the selected station
    if (stationCode) {
      this.store.dispatch(WatershedActions.loadStationSimulationData({ stationId: stationCode }));
    }

    this.catchmentHydroStations$
      .pipe(
        first(stations => !!stations?.length),
        withLatestFrom(this.dataMode$),
        tap(([stations, dataMode]) => {
          const station = stations.find(s => s.id === stationCode);
          const hasDischarge = station?.type === "1" || station?.type === "2";

          if (dataMode.variable === this.dataModeVariable.Discharge && !hasDischarge) {
            this.store.dispatch(WatershedActions.setDataMode({ dataMode: { ...dataMode, variable: this.dataModeVariable.WaterLevel } }));
          }
        }),
      )
      .subscribe();
  }

  public selectPrecipitationStation(catchmentPrecipitationStationCode: string): void {
    this.selectedPrecipitationStationCode = catchmentPrecipitationStationCode;
    this.store.dispatch(WatershedActions.setCatchmentPrecipitationStationCode({ catchmentPrecipitationStationCode }));
  }

  public async switchModalWidth(): Promise<void> {
    this.store.dispatch(AppActions.switchModalWidth());
  }

  public setCatchmentTitle(catchment: string): string {
    let catchmentText: string = "";
    if (catchment) {
      if (catchment === CatchmentNames.Gros || catchment === CatchmentNames.Major) {
        catchmentText = this.translateService.instant("ZONE_NAMES.TORRENT") + catchment;
      } else if (
        catchment.charAt(0) === "A" ||
        catchment.charAt(0) === "E" ||
        catchment.charAt(0) === "I" ||
        catchment.charAt(0) === "O" ||
        catchment.charAt(0) === "U"
      ) {
        catchmentText = this.translateService.instant("ZONE_NAMES.TORRENT_D") + catchment;
      } else if (catchment.includes(CatchmentNames.SantaEularia)) {
        catchmentText = this.translateService.instant("ZONE_NAMES.RIU_DE") + catchment;
      } else if (catchment.includes(IslandsNames.Formentera)) {
        catchmentText = IslandsNames.Formentera;
      } else {
        catchmentText = this.translateService.instant("ZONE_NAMES.TORRENT_DE") + catchment;
      }

      if (this.currentCatchmentId && GROUPPED_CATCHMENTS.includes(this.currentCatchmentId)) {
        catchmentText = catchment;
      }
    }

    return catchmentText;
  }

  public setCatchmentSubtitle(catchment: CatchmentDto): string {
    let catchmentText: string = "";

    if (catchment.id === 11 || catchment.id === 49) {
      catchmentText = catchment.name;
    } else if (
      catchment.name.charAt(0) === "A" ||
      catchment.name.charAt(0) === "E" ||
      catchment.name.charAt(0) === "I" ||
      catchment.name.charAt(0) === "O" ||
      catchment.name.charAt(0) === "U"
    ) {
      catchmentText = this.translateService.instant("ZONE_NAMES.PREPOSITION_D") + catchment.name;
    } else {
      catchmentText = this.translateService.instant("ZONE_NAMES.PREPOSITION_DE") + catchment.name;
    }

    GROUPPED_CATCHMENTS.forEach(catchmentId => {
      if (catchmentId === catchment.id) catchmentText = this.translateService.instant("ZONE_NAMES.TO_THESE_BASINS");
    });
    return catchmentText;
  }

  public setCatchmentSubtitleState(catchment: CatchmentDto): string {
    let catchmentState: string;

    if (catchment) {
      if (
        catchment.id === 11 ||
        catchment.id === 49 ||
        catchment.name.charAt(0) === "A" ||
        catchment.name.charAt(0) === "E" ||
        catchment.name.charAt(0) === "I" ||
        catchment.name.charAt(0) === "O" ||
        catchment.name.charAt(0) === "U"
      ) {
        catchmentState = "torrent";
      } else {
        catchmentState = "torrent";
      }

      GROUPPED_CATCHMENTS.forEach(catchmentId => {
        if (catchmentId === catchment.id) catchmentState = "conques";
      });
    }
    return catchmentState;
  }

  private loadCatchment(catchmentId: number): void {
    this.store.dispatch(WatershedActions.loadAnyCatchmentStations({ catchmentId: catchmentId }));
    this.store.dispatch(WatershedActions.loadAnyCatchmentBlackspots({ catchmentId: catchmentId }));
  }

  private orderByStatus(a: StationDto, b: StationDto): number {
    if (a.status > b.status) {
      return -1;
    }

    if (a.status < b.status) {
      return 1;
    }

    const orderByAlertLevel = new OrderStationsByAlertLevelPipe();

    return orderByAlertLevel.orderStationsByAlertLevel(a, b);
  }
}
